import React, { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import U2000FormLeaveConfirmDialog from '../../components/dialog/U2000FormLeaveConfirmDialog';

type FormLeavePromptHandlers = {
  onConfirm: () => void;
  onCancel: () => void;
};

export interface U2000FormLeaveGuardContextValue {
  isFormLeaveBlocked: boolean;
  setFormLeaveBlocking: (registrationId: string, blocking: boolean) => void;
  unregisterFormLeaveBlocking: (registrationId: string) => void;
  promptFormLeave: (handlers: FormLeavePromptHandlers) => void;
  /** Exécute onProceed immédiatement ou après confirmation si saisie non enregistrée. */
  requestConfirmedLeave: (onProceed: () => void) => void;
  /** Navigation SPA autorisée sans useBlocker (ex. retour liste juste après enregistrement). */
  runNavigationWithoutFormLeaveBlock: (navigateFn: () => void) => void;
  /** Ref lue par useU2000FormLeaveGuard au moment du blocage (BL-0635). */
  navigationBypassRef: React.MutableRefObject<boolean>;
}

const U2000FormLeaveGuardContext = createContext<U2000FormLeaveGuardContextValue | undefined>(undefined);

export const U2000FormLeaveGuardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [blockingById, setBlockingById] = useState<Record<string, boolean>>({});
  const [promptHandlers, setPromptHandlers] = useState<FormLeavePromptHandlers | null>(null);
  const navigationBypassRef = useRef(false);

  const isFormLeaveBlocked = useMemo(() => Object.values(blockingById).some(Boolean), [blockingById]);

  const setFormLeaveBlocking = useCallback((registrationId: string, blocking: boolean) => {
    setBlockingById((prev) => {
      if (!blocking) {
        if (!(registrationId in prev)) {
          return prev;
        }
        const next = { ...prev };
        delete next[registrationId];
        return next;
      }
      if (prev[registrationId] === blocking) {
        return prev;
      }
      return { ...prev, [registrationId]: blocking };
    });
  }, []);

  const unregisterFormLeaveBlocking = useCallback((registrationId: string) => {
    setBlockingById((prev) => {
      if (!(registrationId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[registrationId];
      return next;
    });
  }, []);

  const promptFormLeave = useCallback((handlers: FormLeavePromptHandlers) => {
    setPromptHandlers(handlers);
  }, []);

  const requestConfirmedLeave = useCallback(
    (onProceed: () => void) => {
      if (!isFormLeaveBlocked) {
        onProceed();
        return;
      }
      promptFormLeave({
        onConfirm: () => {
          setPromptHandlers(null);
          onProceed();
        },
        onCancel: () => setPromptHandlers(null),
      });
    },
    [isFormLeaveBlocked, promptFormLeave],
  );

  const runNavigationWithoutFormLeaveBlock = useCallback((navigateFn: () => void) => {
    navigationBypassRef.current = true;
    try {
      navigateFn();
    } finally {
      queueMicrotask(() => {
        navigationBypassRef.current = false;
      });
    }
  }, []);

  const handleDialogConfirm = () => {
    promptHandlers?.onConfirm();
    setPromptHandlers(null);
  };

  const handleDialogCancel = () => {
    promptHandlers?.onCancel();
    setPromptHandlers(null);
  };

  const value = useMemo(
    () => ({
      isFormLeaveBlocked,
      setFormLeaveBlocking,
      unregisterFormLeaveBlocking,
      promptFormLeave,
      requestConfirmedLeave,
      runNavigationWithoutFormLeaveBlock,
      navigationBypassRef,
    }),
    [
      isFormLeaveBlocked,
      setFormLeaveBlocking,
      unregisterFormLeaveBlocking,
      promptFormLeave,
      requestConfirmedLeave,
      runNavigationWithoutFormLeaveBlock,
    ],
  );

  return (
    <U2000FormLeaveGuardContext.Provider value={value}>
      {children}
      <U2000FormLeaveConfirmDialog
        isOpen={promptHandlers !== null}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
    </U2000FormLeaveGuardContext.Provider>
  );
};

export function useU2000FormLeaveGuardContext(): U2000FormLeaveGuardContextValue {
  const ctx = useContext(U2000FormLeaveGuardContext);
  if (!ctx) {
    throw new Error('useU2000FormLeaveGuardContext doit être utilisé sous U2000FormLeaveGuardProvider');
  }
  return ctx;
}
